import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './MovementModeControls.css';

const MovementModeControls = ({
  visible,
  isGameMaster,
  combatActive,
  mode,
  intent,
  onModeChange,
  onIntentChange,
  onClose = null,
  triggerRef = null,
  anchorRect = null
}) => {
  const panelRef = useRef(null);

  const closePanel = useCallback(() => {
    onClose?.();
    window.requestAnimationFrame?.(() => triggerRef?.current?.focus?.());
  }, [onClose, triggerRef]);

  useEffect(() => {
    if (!visible || typeof onClose !== 'function') return undefined;
    const handlePointerDown = (event) => {
      if (!panelRef.current?.contains(event.target) && !triggerRef?.current?.contains?.(event.target)) {
        closePanel();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    panelRef.current?.querySelector('select')?.focus?.();
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePanel, onClose, triggerRef, visible]);

  const panelStyle = useMemo(() => {
    if (!anchorRect) return undefined;
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 720;
    const width = Math.min(360, viewportWidth - 24);
    const left = Math.max(12, Math.min(anchorRect.left, viewportWidth - width - 12));
    const estimatedHeight = 250;
    const opensAbove = anchorRect.bottom + estimatedHeight > viewportHeight - 12;
    return {
      position: 'fixed',
      left,
      top: opensAbove ? Math.max(12, anchorRect.top - estimatedHeight - 6) : anchorRect.bottom + 6
    };
  }, [anchorRect]);

  if (!visible) return null;

  const panel = (
    <section
      ref={panelRef}
      className="br-movement-mode"
      role="dialog"
      aria-label="Movement options"
      style={panelStyle}
      data-testid="battle-toolbar-movement-panel"
    >
      <header className="br-movement-mode__header">
        <div>
          <span className="br-movement-mode__eyebrow">Next token drag</span>
          <h2>Movement Options</h2>
        </div>
        {onClose && (
          <button type="button" onClick={closePanel} aria-label="Close movement options">×</button>
        )}
      </header>
      <div className="br-movement-mode__body">
        <p className="br-movement-mode__intro">
          Ordinary dragging uses walking and automatically chooses setup placement before combat or normal movement during combat.
        </p>
        <label>
          Movement mode
          <select value={mode} onChange={(event) => onModeChange(event.target.value)}>
            <option value="walk">Walk</option>
            <option value="climb">Climb</option>
            <option value="swim">Swim</option>
            <option value="fly">Fly</option>
          </select>
        </label>
        <label>
          Movement intent
          <select value={intent} onChange={(event) => onIntentChange(event.target.value)}>
            <option value="auto">Automatic ({combatActive ? 'normal movement' : 'setup placement'})</option>
            <option value="normal">Normal movement</option>
            {!combatActive && <option value="setup">Setup placement</option>}
            {isGameMaster && <option value="forced">Forced movement</option>}
            {isGameMaster && <option value="teleport">Teleport</option>}
            {isGameMaster && <option value="correction">GM correction</option>}
          </select>
        </label>
        {intent !== 'auto' && (
          <p className="br-movement-mode__armed" role="status">
            Armed: the next token drag uses {intent.replace('_', ' ')} intent, then returns to automatic.
          </p>
        )}
      </div>
    </section>
  );

  if (typeof document === 'undefined' || !document.body) return panel;
  return createPortal(panel, document.body);
};

MovementModeControls.propTypes = {
  visible: PropTypes.bool.isRequired,
  isGameMaster: PropTypes.bool,
  combatActive: PropTypes.bool,
  mode: PropTypes.string.isRequired,
  intent: PropTypes.string.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onIntentChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  triggerRef: PropTypes.shape({ current: PropTypes.any }),
  anchorRect: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    bottom: PropTypes.number,
    right: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number
  })
};

export default MovementModeControls;
